import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import useSWRImmutable from 'swr/immutable'
import clsx from 'clsx'
import { SpoilerChevron } from '@ui/icons/common'
import type { CategoryFacet } from '@model/product'
import s from './CategoryList.module.scss'
import { MAX_MANUFACTURER_FACETS } from '@components/category/CategorySidePanel/CategorySidePanel'
import type { CategoryTip } from '@model/category/CategoryTip'
import { getBranchByCategoryId } from '@utils/categories'
import { keys, pickAll, reduce } from 'ramda'
import { useTranslate } from '@hooks/useTranslation'
import { getSlug } from '@utils/slugs'
import { useRouter } from 'next/router'
import { CwsP, CwsSpan } from '@components/cws'
const separator = ' -> '

const categoryReducer = (allCategoryTree: CategoryTip[]) =>
  reduce((acc, val: TempCategoryInfo) => {
    if (val.invisible) return acc
    const tree = getBranchByCategoryId(val.parentId, allCategoryTree)
    let catName: string
    if (!tree.length) {
      return acc
      // SHOP-3131
      // catName = `${val.id}#${val.name} (${val.numberOfProducts})`
      // return {
      //   ...acc,
      //   [catName]: [],
      // }
    } else {
      if (tree.length > 1) {
        const [lvl1, lvl2] = tree
        const lvl1Slug = getSlug(lvl1.name, lvl1.id)
        const lvl2Slug = getSlug(lvl2.name, lvl2.id)
        catName = `${lvl1?.name}#${lvl1Slug}${separator}${lvl2?.name}#${lvl2Slug}`
      } else {
        return acc
      }
    }

    return {
      ...acc,
      [catName]: [`${val.id}#${val.name} (${val.numberOfProducts})`, ...(acc[catName] || [])],
    }
  }, {} as ResultTree)

const CategoryItem: React.FC<{
  id: number | string
  name: string
  currentCategoryId?: number
  slug: string
}> = ({ id, name, currentCategoryId, slug }) => {
  return (
    <li key={`tip-tree-item-${id}`}>
      <Link href={`/category/${slug}`} passHref legacyBehavior>
        <CwsSpan className="hover:opacity-75" color="midnight">
          {name}
        </CwsSpan>
      </Link>
    </li>
  )
}

interface CategoryListProps {
  categories: CategoryFacet[]
  currentCategoryId?: number
}

type TempCategoryInfo = {
  id: number
  parentId: number
  name: string
  numberOfProducts: number
  invisible: boolean
}

type ResultTree = { [key: string]: string[] }

export const CategoryList: React.FC<CategoryListProps> = ({ categories, currentCategoryId }) => {
  const [expandedCategories, setExpandedCategories] = useState(false)

  const { locale } = useRouter()
  const { translate } = useTranslate()
  const { data: allCategoryTree, error: allCategoryError } = useSWRImmutable<CategoryTip[]>(
    `/api/category/?locale=${locale}`
  )

  const categoriesToRender = useMemo(() => {
    return expandedCategories ? categories : categories.slice(0, MAX_MANUFACTURER_FACETS)
  }, [categories, expandedCategories])

  const treeForRender = useMemo(() => {
    if (!allCategoryTree) return {}

    const src = categoriesToRender.map(pickAll(['id', 'name', 'parentId', 'numberOfProducts', 'invisible']))
    return categoryReducer(allCategoryTree)(src as TempCategoryInfo[])
  }, [categoriesToRender, allCategoryTree])

  if (!allCategoryTree || allCategoryError) {
    return null
  }

  return (
    <>
      <ul className={clsx(s.container, 'flex flex-col space-y-2')}>
        {keys(treeForRender).map((key) => {
          if (key && !treeForRender[key].length) {
            const [id, name] = (key as string).split('#')
            const slug = getSlug(name, id)
            return <CategoryItem key={key} name={name} id={id} currentCategoryId={currentCategoryId} slug={slug} />
          }

          const [lvl1, lvl2] = (key as string).split(separator)
          const [lvl1Name, lvl1Link] = lvl1.split('#')
          const [lvl2Name, lvl2Link] = lvl2.split('#')

          return (
            <li className="list-none" key={key}>
              <CwsP className={clsx(s.lvl1, 'hover:opacity-75')}>
                <Link href={`/category/${lvl1Link}`} passHref>
                  {lvl1Name}
                </Link>
              </CwsP>
              <CwsP className={clsx(s.lvl2, 'hover:opacity-75')}>
                <Link href={`/category/${lvl2Link}`} passHref>
                  {lvl2Name}
                </Link>
              </CwsP>
              <ul className={s.treeList}>
                {treeForRender[key].map((lowerCategory) => {
                  const [id, name] = lowerCategory.split('#')
                  const slug = getSlug(name, id)
                  return <CategoryItem key={id} name={name} id={id} currentCategoryId={currentCategoryId} slug={slug} />
                })}
              </ul>
            </li>
          )
        })}
      </ul>
      {categories.length > 12 && (
        <button
          className="flex flex-row space-x-2 items-center"
          onClick={setExpandedCategories.bind(this, !expandedCategories)}
        >
          <SpoilerChevron width={13} height={13} open={expandedCategories} />
          <span className={'font-medium text-midnight text-sm leading-4 underline'}>
            {expandedCategories ? translate('filters.brandname.show.less') : translate('filters.brandname.show.all')}
          </span>
        </button>
      )}
    </>
  )
}
